<template>
  <!-- Keşfet (DiscoveryGrid) kartlarındaki aksiyon butonlarının aynısı: arama (cameraOn/voiceOn), sohbet, beğeni.
       Davranış DiscoveryGrid ile birebir aynıdır; farklı sayfalarda aynı görünüm ve çalışma için ortak bileşen. -->
  <div class="card-actions-bar user-card-actions">
    <!-- Sesli arama: AI kullanıcılar veya cameraOn false + voiceOn true -->
    <button
      v-if="getCallMode(user) === 'voice'"
      class="action-pill-btn call-action voice-action"
      @click.stop="call(user)"
      :title="$t('Voice Call') || 'Voice call'"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
      </svg>
      <span>{{ $t('Voice Call') || 'Voice call' }}</span>
    </button>
  
    <!-- Görüntülü arama: cameraOn true -->
    <button
      v-else-if="getCallMode(user) === 'video'"
      class="action-pill-btn call-action video-action"
      @click.stop="videoCall(user)"
      :title="$t('Video Call') || 'Video call'"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="23 7 16 12 23 17 23 7"></polygon>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
      </svg>
      <span>{{ $t('Video Call') || 'Video call' }}</span>
    </button>
  
    <!-- Chat Button -->
    <button
      class="action-circle-btn chat-action"
      @click.stop="chat(user)"
      :title="$t('Chat') || 'Chat'"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
    </button>
  
    <!-- Like / Heart Button -->
    <button
      class="action-circle-btn like-action"
      :class="{ 'is-liked': user.is_followed || user.is_liked }"
      @click.stop="toggleFollow"
      :title="$t('Like') || 'Like'"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" :fill="(user.is_followed || user.is_liked) ? '#ef4444' : 'none'" :stroke="(user.is_followed || user.is_liked) ? '#ef4444' : 'currentColor'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    </button>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { showNotify, throwDefaultError } from '@/helpers/helpers'
import userCallActions from '@/mixins/userCallActions'

export default {
  name: 'UserCardActions',
  mixins: [userCallActions],
  props: {
    user: {
      type: Object,
      required: true,
    },
    // Arama istatistikleri için hangi sayfadan arandığı
    callSource: {
      type: String,
      default: 'discovery_grid',
    },
  },
  methods: {
    toggleFollow() {
      const targetUser = this.user
      const targetId = targetUser.uuid || targetUser.id
      if (!targetUser.is_followed) {
        useJwt.addFavoriteUser(targetId)
          .then(() => {
            showNotify(this.$toast, this.$t('Liking successful') || 'Success', 'success')
            this.$set(targetUser, 'is_followed', true)
            this.$set(targetUser, 'is_liked', true)
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
      } else {
        useJwt.removeFavoriteUser(targetId)
          .then(() => {
            showNotify(this.$toast, this.$t('Unliking successful') || 'Removed', 'success')
            this.$set(targetUser, 'is_followed', false)
            this.$set(targetUser, 'is_liked', false)
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.card-actions-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  // Kart genişliğine göre butonları sığdırmak için (aşağıdaki @container kuralı)
  container-type: inline-size;

  @media (max-width: 576px) {
    gap: 4px;
  }

  // Arama butonunun yazısı sığmazsa "…" ile kesilsin (container query desteklemeyen tarayıcılar için de güvenli)
  .action-pill-btn span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .action-pill-btn svg,
  .action-circle-btn svg {
    flex-shrink: 0;
  }


  .action-pill-btn {
    flex: 1;
    min-width: 0;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border: none;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0 8px;
    white-space: nowrap;

    @media (max-width: 576px) {
      height: 28px;
      padding: 0 4px;
      font-size: 10px;
      gap: 3px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    &:active {
      transform: scale(0.96);
    }
  }

  .action-circle-btn {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 9999px;
    border: none;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;

    @media (max-width: 576px) {
      width: 28px;
      height: 28px;
    }

    .plus-icon {
      font-size: 16px;
      line-height: 1;
      font-weight: 700;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    &:active {
      transform: scale(0.92);
    }

    &.is-active {
      background: #7c3aed;
      color: #ffffff;
    }

    &.is-liked {
      background: rgba(239, 68, 68, 0.2);
    }
  }
}

// Dar kartlarda (mobil 2 sütun ızgara) arama butonunun yazısı gizlenir, sadece ikon kalır; butonlar taşmaz
@container (max-width: 200px) {
  .user-card-actions .action-pill-btn span {
    display: none;
  }
}
</style>
